/**
 * Persist a 'grid' | 'list' view-mode choice in localStorage, scoped per
 * page key. Lives in its own file so HMR / fast-refresh stays happy with
 * ViewToggle (which only exports components).
 */
import { useEffect, useState } from 'react'

export type ViewMode = 'grid' | 'list'

const STORAGE_PREFIX = 'v2:view-mode:'

export function useViewMode(
  pageKey: string,
  defaultMode: ViewMode = 'grid',
): [ViewMode, (m: ViewMode) => void] {
  const storageKey = STORAGE_PREFIX + pageKey
  const [mode, setMode] = useState<ViewMode>(defaultMode)

  useEffect(() => {
    try {
      const v = window.localStorage.getItem(storageKey)
      if (v === 'grid' || v === 'list') setMode(v)
    } catch {
      // localStorage unavailable — fall back to default
    }
  }, [storageKey])

  function set(next: ViewMode) {
    setMode(next)
    try {
      window.localStorage.setItem(storageKey, next)
    } catch {
      // ignore
    }
  }

  return [mode, set]
}
